import {ContentContainer, useMessage} from '@coolcolduk/ui-mui';
import {Trade, TradeDirection, TradeStatus} from '@cp/model';
import {Delete, Edit, ExpandMore, Save} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {getDownloadURL, ref as storageRef} from 'firebase/storage';
import {useCallback, useEffect, useState} from 'react';
import {deleteTrade} from '../../../api/trade/deleteTrade';
import {listTrades} from '../../../api/trade/listTrades';
import {updateTrade} from '../../../api/trade/updateTrade';
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';
import {firebaseStorage} from '../../../config/firebase';
import AdminTradeCreateForm from './AdminTradeCreateForm';

interface EditableTrade extends Trade {
  isEditing?: boolean;
}

function tradeStatusToLabel(status: TradeStatus): string {
  if (status === TradeStatus.OPEN) return 'Open';
  if (status === TradeStatus.CLOSED) return 'Closed';
  if (status === TradeStatus.STOPLOSS) return 'SL';
  if (status === TradeStatus.TAKEPROFIT) return 'TP';
  return status;
}

const parseTags = (input: string): string[] =>
  input
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

function calculateTradePnL(trade: Trade): number | null {
  const {entryPrice, exitPrice, positionSize, direction} = trade;
  if (!Number.isFinite(entryPrice)) return null;
  if (exitPrice == null || !Number.isFinite(exitPrice)) return null;
  if (positionSize == null || !Number.isFinite(positionSize)) return null;

  const diff = direction === TradeDirection.LONG ? exitPrice - entryPrice : entryPrice - exitPrice;
  return diff * positionSize;
}

function TradeMessageImage({screenshotStoragePath}: {screenshotStoragePath?: unknown}) {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!screenshotStoragePath) {
      setResolvedUrl(null);
      return;
    }

    const resolvedStoragePath =
      typeof screenshotStoragePath === 'string' && screenshotStoragePath.length > 0 ? screenshotStoragePath : null;

    if (!resolvedStoragePath) {
      setResolvedUrl(null);
      return;
    }

    // If it's already a URL, we can render directly.
    if (
      resolvedStoragePath.startsWith('http://') ||
      resolvedStoragePath.startsWith('https://') ||
      resolvedStoragePath.startsWith('data:')
    ) {
      setResolvedUrl(resolvedStoragePath);
      return;
    }

    // Otherwise, assume it's a Firebase Storage path and resolve to a download URL.
    const run = async () => {
      try {
        setLoading(true);
        const url = await getDownloadURL(storageRef(firebaseStorage, resolvedStoragePath));
        setResolvedUrl(url);
      } catch (err) {
        console.error('Failed to resolve screenshot URL', err);
        setResolvedUrl(null);
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [screenshotStoragePath]);

  if (!screenshotStoragePath) return null;

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 120,
          bgcolor: 'grey.100',
          borderRadius: 1,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!resolvedUrl) return null;

  return (
    <Box
      component="img"
      src={resolvedUrl}
      alt="Trade message"
      sx={{
        maxWidth: '100%',
        maxHeight: 360,
        objectFit: 'contain',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
      }}
    />
  );
}

export default function AdminTradePage() {
  const {showMessage} = useMessage();
  const [trades, setTrades] = useState<EditableTrade[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const loadTrades = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listTrades();
      setTrades(data);
    } catch (error) {
      console.error('Failed to load trades', error);
      showMessage('Failed to load trades', 6000, 'error');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    void loadTrades();
  }, [loadTrades]);

  const handleEditToggle = (id: string, editing: boolean) => {
    setTrades((prev) => prev.map((trade) => (trade.id === id ? {...trade, isEditing: editing} : trade)));
  };

  const handleFieldChange = <K extends keyof EditableTrade>(id: string, field: K, value: EditableTrade[K]) => {
    setTrades((prev) => prev.map((trade) => (trade.id === id ? {...trade, [field]: value} : trade)));
  };

  const handleSave = async (trade: EditableTrade) => {
    if (!trade.symbol.trim()) {
      showMessage('Symbol is required', 4000, 'error');
      return;
    }
    if (!Number.isFinite(trade.entryPrice) || Number.isNaN(trade.entryPrice)) {
      showMessage('Entry (open) price must be a valid number', 4000, 'error');
      return;
    }
    if (trade.status !== TradeStatus.OPEN && trade.exitPrice == null) {
      showMessage('Exit (close) price is required for closed trades', 4000, 'error');
      return;
    }
    setSavingId(trade.id);
    try {
      const update: {
        symbol: string;
        direction: TradeDirection;
        status: TradeStatus;
        entryPrice: number;
        sessionId?: string;
        exitPrice?: number;
        tags: string[];
      } = {
        symbol: trade.symbol.trim(),
        direction: trade.direction,
        status: trade.status,
        entryPrice: trade.entryPrice,
        tags: trade.tags,
      };

      if (trade.sessionId != null) {
        update.sessionId = trade.sessionId;
      }
      if (trade.exitPrice != null) {
        update.exitPrice = trade.exitPrice;
      }

      await updateTrade(trade.id, update);
      setTrades((prev) => prev.map((t) => (t.id === trade.id ? {...trade, isEditing: false} : t)));
      showMessage('Trade updated', 4000, 'success');
    } catch (error) {
      console.error('Failed to update trade', error);
      showMessage('Failed to update trade', 6000, 'error');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setSavingId(id);
    try {
      await deleteTrade(id);
      setTrades((prev) => prev.filter((trade) => trade.id !== id));
      showMessage('Trade deleted', 4000, 'success');
    } catch (error) {
      console.error('Failed to delete trade', error);
      showMessage('Failed to delete trade', 6000, 'error');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <ContentContainer data-testid="admin-trade-page">
      <Typography variant="h5" component="h2" sx={{mb: 2}}>
        Trades
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
        View and manage trades captured via the Trading Journal extension.
      </Typography>

      <AdminTradeCreateForm
        onTradeCreated={(trade) => {
          setTrades((prev) => [...prev, {...trade}]);
        }}
      />

      <Stack spacing={1}>
        {trades.map((trade) => {
          const pnl = calculateTradePnL(trade);
          const pnlText = pnl == null ? '—' : `${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}`;
          const pnlColor = pnl == null ? 'text.secondary' : pnl >= 0 ? 'success.main' : 'error.main';

          return (
            <Accordion key={trade.id} disableGutters>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`trade-${trade.id}-content`}
                id={`trade-${trade.id}-header`}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{width: '100%', pr: 1}}>
                  <Stack sx={{flex: 1, minWidth: 220}}>
                    <Typography variant="subtitle2">{trade.symbol}</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{mt: 0.25}}>
                      <Chip size="small" label={trade.direction === TradeDirection.LONG ? 'Long' : 'Short'} />
                      <Chip size="small" label={tradeStatusToLabel(trade.status)} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Session: {trade.sessionId ?? '—'}
                    </Typography>
                  </Stack>

                  <Stack sx={{minWidth: 120, alignItems: 'flex-end'}}>
                    <Typography variant="body2" sx={{color: pnlColor, fontWeight: 700}}>
                      P/L: {pnlText}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                    {trade.isEditing ? (
                      <IconButton
                        component="span"
                        aria-label="Save"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleSave(trade);
                        }}
                        disabled={savingId === trade.id}
                        size="small"
                      >
                        <Save fontSize="small" />
                      </IconButton>
                    ) : (
                      <IconButton
                        component="span"
                        aria-label="Edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditToggle(trade.id, true);
                        }}
                        size="small"
                        disabled={savingId != null}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    )}

                    <IconButton
                      component="span"
                      aria-label="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteId(trade.id);
                      }}
                      size="small"
                      disabled={savingId === trade.id}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </AccordionSummary>

              <AccordionDetails>
                <Stack spacing={2}>
                  {trade.isEditing ? (
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} flexWrap="wrap">
                      <TextField
                        label="Symbol"
                        size="small"
                        value={trade.symbol}
                        onChange={(e) => handleFieldChange(trade.id, 'symbol', e.target.value)}
                        sx={{minWidth: {xs: '100%', sm: 160}}}
                      />

                      <FormControl size="small" sx={{minWidth: 160}}>
                        <InputLabel id={`dir-${trade.id}`}>Direction</InputLabel>
                        <Select
                          labelId={`dir-${trade.id}`}
                          value={trade.direction}
                          label="Direction"
                          onChange={(e) => handleFieldChange(trade.id, 'direction', e.target.value as TradeDirection)}
                        >
                          <MenuItem value={TradeDirection.LONG}>Long</MenuItem>
                          <MenuItem value={TradeDirection.SHORT}>Short</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl size="small" sx={{minWidth: 200}}>
                        <InputLabel id={`status-${trade.id}`}>Close type</InputLabel>
                        <Select
                          labelId={`status-${trade.id}`}
                          value={trade.status}
                          label="Close type"
                          onChange={(e) => handleFieldChange(trade.id, 'status', e.target.value as TradeStatus)}
                        >
                          <MenuItem value={TradeStatus.OPEN}>Open</MenuItem>
                          <MenuItem value={TradeStatus.CLOSED}>Closed (manual)</MenuItem>
                          <MenuItem value={TradeStatus.STOPLOSS}>Closed (stop loss)</MenuItem>
                          <MenuItem value={TradeStatus.TAKEPROFIT}>Closed (take profit)</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        label="Entry (open) price"
                        size="small"
                        type="number"
                        value={Number.isFinite(trade.entryPrice) ? trade.entryPrice : ''}
                        onChange={(e) => handleFieldChange(trade.id, 'entryPrice', Number(e.target.value))}
                        sx={{minWidth: {xs: '100%', sm: 220}}}
                      />

                      <TextField
                        label="Exit (close) price"
                        size="small"
                        type="number"
                        value={trade.exitPrice ?? ''}
                        onChange={(e) =>
                          handleFieldChange(
                            trade.id,
                            'exitPrice',
                            e.target.value.trim() === '' ? undefined : Number(e.target.value),
                          )
                        }
                        disabled={trade.status === TradeStatus.OPEN}
                        sx={{minWidth: {xs: '100%', sm: 220}}}
                      />

                      <TextField
                        label="Tags (comma separated)"
                        size="small"
                        value={(trade.tags ?? []).join(', ')}
                        onChange={(e) => handleFieldChange(trade.id, 'tags', parseTags(e.target.value))}
                        sx={{minWidth: {xs: '100%', sm: 320}}}
                      />
                    </Stack>
                  ) : (
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} flexWrap="wrap">
                      <Typography variant="body2" color="text.secondary">
                        Opened at: {trade.openedAt ? trade.openedAt.toISOString() : '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Entry: {Number.isFinite(trade.entryPrice) ? trade.entryPrice : '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Exit: {trade.exitPrice ?? '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Tags: {(trade.tags ?? []).join(', ') || '—'}
                      </Typography>
                    </Stack>
                  )}

                  <Divider />

                  <Stack spacing={1}>
                    <Typography variant="subtitle1">Messages</Typography>
                    {trade.messages.map((msg, index) => (
                      <Box
                        key={msg.createdAt ? msg.createdAt.toISOString() : `${trade.id}-${index}`}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 2,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {msg.createdAt ? msg.createdAt.toISOString() : '—'}
                        </Typography>
                        <Typography variant="body2" sx={{mt: 0.5}}>
                          {msg.note?.trim() ? msg.note : '—'}
                        </Typography>
                        <Box sx={{mt: 1}}>
                          <TradeMessageImage screenshotStoragePath={msg.screenshotStoragePath} />
                        </Box>
                      </Box>
                    ))}
                    {trade.messages.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No messages yet.
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {trades.length === 0 && !loading && (
          <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
            No trades yet.
          </Typography>
        )}
      </Stack>

      <ConfirmationDialog
        title="Delete trade"
        content="Are you sure you want to delete this trade?"
        confirmButtonLabel="Delete"
        confirmButtonColor="error"
        open={deleteId != null}
        loading={savingId === deleteId}
        handleConfirm={() => {
          if (!deleteId) return;
          void handleDelete(deleteId);
          setDeleteId(null);
        }}
        handleCancel={() => setDeleteId(null)}
        onClose={() => setDeleteId(null)}
      />
    </ContentContainer>
  );
}
